/**
 * Mock Server Status Indicator
 *
 * Optional visual indicator for mock server status
 */

import { useState, useEffect } from 'react';
import { mockServerController, type MockServerStatus } from '@/lib/mock-control';

interface MockServerIndicatorProps {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  compact?: boolean;
}

export function MockServerIndicator({
  position = 'top-right',
  compact = false
}: MockServerIndicatorProps) {
  const [status, setStatus] = useState<MockServerStatus>(mockServerController.getStatus());
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    // Update status every 1 second
    const interval = setInterval(() => {
      setStatus(mockServerController.getStatus());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Don't show in production
  if (import.meta.env.PROD) return null;

  const positionStyles = {
    'top-right': { top: 10, right: 10 },
    'top-left': { top: 10, left: 10 },
    'bottom-right': { bottom: 10, right: 10 },
    'bottom-left': { bottom: 10, left: 10 },
  };

  const baseStyles = {
    position: 'fixed' as const,
    zIndex: 9999,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    color: 'white',
    padding: compact ? '5px 8px' : '8px 12px',
    borderRadius: '6px',
    fontSize: compact ? '10px' : '12px',
    fontFamily: 'monospace',
    cursor: 'pointer',
    border: `2px solid ${status.isRunning ? '#22c55e' : '#ef4444'}`,
    transition: 'all 0.2s ease',
    ...positionStyles[position],
  };

  const handleClick = () => {
    if (compact) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleStart = async () => {
    try {
      await mockServerController.start();
      setStatus(mockServerController.getStatus());
    } catch (error) {
      console.error('Failed to start mock server:', error);
    }
  };

  const handleStop = async () => {
    try {
      await mockServerController.stop();
      setStatus(mockServerController.getStatus());
    } catch (error) {
      console.error('Failed to stop mock server:', error);
    }
  };

  const handleToggleUrl = () => {
    const shouldEnable = !mockServerController.shouldEnableFromUrl();
    mockServerController.updateUrlParameter(shouldEnable);

    if (shouldEnable) {
      window.location.reload();
    }
  };

  if (compact && !isExpanded) {
    return (
      <div style={baseStyles} onClick={handleClick} title="Click to expand mock server controls">
        🎭 {status.isRunning ? '🟢' : '🔴'}
      </div>
    );
  }

  return (
    <div style={baseStyles}>
      <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
        🎭 Mock Server
      </div>

      <div style={{ marginBottom: '8px' }}>
        Status: {status.isRunning ? (
          <span style={{ color: '#22c55e' }}>🟢 Running</span>
        ) : (
          <span style={{ color: '#ef4444' }}>🔴 Stopped</span>
        )}
      </div>

      {status.isRunning && status.startedVia && (
        <div style={{ marginBottom: '8px', fontSize: '10px', opacity: 0.8 }}>
          Started via: {status.startedVia}
          {status.startedAt && (
            <div>At: {new Date(status.startedAt).toLocaleTimeString()}</div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
        <button
          onClick={handleStart}
          disabled={status.isRunning}
          style={{
            background: status.isRunning ? '#666' : '#22c55e',
            color: 'white',
            border: 'none',
            padding: '4px 8px',
            borderRadius: '3px',
            fontSize: '10px',
            cursor: status.isRunning ? 'not-allowed' : 'pointer',
          }}
        >
          Start
        </button>

        <button
          onClick={handleStop}
          disabled={!status.isRunning}
          style={{
            background: !status.isRunning ? '#666' : '#ef4444',
            color: 'white',
            border: 'none',
            padding: '4px 8px',
            borderRadius: '3px',
            fontSize: '10px',
            cursor: !status.isRunning ? 'not-allowed' : 'pointer',
          }}
        >
          Stop
        </button>

        <button
          onClick={handleToggleUrl}
          style={{
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            padding: '4px 8px',
            borderRadius: '3px',
            fontSize: '10px',
            cursor: 'pointer',
          }}
        >
          {mockServerController.shouldEnableFromUrl() ? 'Remove ?mock=true' : 'Add ?mock=true'}
        </button>
      </div>

      {compact && (
        <div
          style={{
            position: 'absolute',
            top: '5px',
            right: '5px',
            cursor: 'pointer',
            fontSize: '10px',
          }}
          onClick={handleClick}
        >
          ✕
        </div>
      )}
    </div>
  );
}

export default MockServerIndicator;